import { ReactNode, useCallback } from "react";

import { Document } from "@/api/rest/models/document";
import { deleteDocument, downloadDocument2, getDocumentUrl2 } from "@/api/rest/services/documentService";
import { DocumentPreviewModal, isPreviewableDocument } from "@/components/household-contents/document/DocumentViewer";

interface DocumentsPreviewProps {
  document: Document;
  buildingId?: number;
  householdId?: number;
  className?: string;
  children: ReactNode;
  onSelect: (url: string, originalName: string) => void;
  allowPreview: boolean;
  allowDelete?: boolean;
  updateHousehold?: () => Promise<any>;
}

export const DocumentsPreview = ({
  document,
  buildingId,
  householdId,
  className,
  children,
  onSelect,
  allowPreview,
  allowDelete = false,
  updateHousehold,
}: DocumentsPreviewProps) => {
  const handleDelete = useCallback(
    async (documentId: number) => {
      if (allowDelete && buildingId && householdId) {
        try {
          await deleteDocument(buildingId, householdId, documentId);
          await updateHousehold?.();
        } catch (error) {
          console.error("Failed to delete document:", error);
        }
      }
    },
    [allowDelete, buildingId, householdId, updateHousehold],
  );

  const handleSelect = useCallback(
    (url: string, originalName: string) => {
      if (onSelect) {
        onSelect(url, originalName);
      } else {
        downloadDocument2(url, originalName);
      }
    },
    [onSelect],
  );

  if (!allowPreview || !isPreviewableDocument(document)) {
    return (
      <div className={className} onClick={() => handleSelect(getDocumentUrl2(document), document.originalFilename)}>
        {children}
      </div>
    );
  }

  return (
    <DocumentPreviewModal
      document={document}
      buildingId={buildingId}
      householdId={householdId}
      trigger={<div className={className}>{children}</div>}
      onDownload={handleSelect}
      onDelete={allowDelete ? handleDelete : undefined}
    />
  );
};
